/**
 * Formata o título de um anúncio de emprego para exibição.
 *
 * A API entrega o título em "sentence case" (só a primeira letra maiúscula):
 * "Auxiliar de limpeza - são paulo", "Motorista pcd", "Garçom/garçonete".
 * No card isso esconde a cidade e a sigla e deixa o cargo com cara de frase.
 * Aqui o título vira "Title Case" com as regras do português:
 *
 *   - cada palavra ganha inicial maiúscula ("Auxiliar de Limpeza");
 *   - conectivos ficam minúsculos, exceto no início ou depois de um separador
 *     ("Servente de Obras - Barueri", "Auxiliar de Atendimento - Sem Experiência");
 *   - siglas conhecidas ficam em caixa alta ("Motorista PCD", "Lapa-SP", "CNC II");
 *   - o sufixo de gênero fica como está ("Vendedor(a)", "Cozinheiros(as)");
 *   - espaços duplicados, espaços dentro dos parênteses e parênteses colados
 *     são normalizados ("Fiscal de loja ( masculino )" → "Fiscal de Loja (Masculino)",
 *     "produção(homem)empresa" → "Produção (Homem) Empresa").
 *
 * Só afeta a exibição. O `slug`, a busca e o que vai para a API continuam
 * usando o título original. Também funciona se um dia o título vier em caixa
 * alta, porque tudo é rebaixado antes de capitalizar.
 */

const LOWERCASE_WORDS = new Set([
  'a', 'à', 'as', 'às', 'o', 'os', 'ao', 'aos',
  'de', 'da', 'das', 'do', 'dos',
  'e', 'ou',
  'em', 'no', 'na', 'nos', 'nas',
  'para', 'pra', 'por', 'pelo', 'pela', 'pelos', 'pelas',
  'com', 'sem',
  'um', 'uma',
]);

const UPPERCASE_WORDS = new Set([
  // vaga / contrato / documentos
  'pcd', 'clt', 'pj', 'mei', 'cnh', 'cnpj', 'cpf',
  // áreas / setores / normas
  'ti', 'rh', 'dp', 'adm', 'sac', 'pcp', 'ppcp', 'flv', 'cd', 'cpd', 'qa', 'ux', 'ui',
  'erp', 'crm', 'sap', 'bi', 'api', 'sql', 'html', 'css', 'php',
  'cnc', 'cftv', 'pabx', 'hvac', 'nr', 'nr10', 'nr33', 'nr35', 'sesmt', 'cipa', 'tst',
  'mopp', 'abnt', 'iso', 'kpi', 'lgpd', 'esg', 'b2b', 'b2c', 'epi', 'epc', 'ssma', 'sst',
  // estados
  'ac', 'al', 'ap', 'am', 'ba', 'ce', 'df', 'es', 'go', 'ma', 'mt', 'ms', 'mg', 'pa', 'pb',
  'pr', 'pe', 'pi', 'rj', 'rn', 'rs', 'ro', 'rr', 'sc', 'sp', 'se', 'to',
  // numeração romana
  'ii', 'iii', 'iv', 'vi', 'vii', 'viii',
]);

/** Sufixo de gênero colado ao cargo: "(a)", "(as)", "(o)", "(os)", "(ao)". */
const GENDER_SUFFIX = /^\((a|as|o|os|ao|à)\)$/i;

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * Capitaliza uma "palavra" com a pontuação que veio grudada nela:
 * "serralheiro(a)", "lapa-sp", "pós-vendas", "geral/servente", "(homem)".
 */
function capitalizeWord(word: string, forceCapital: boolean): string {
  const tokens = word.match(/[\p{L}\p{N}]+/gu) ?? [];
  const isSingleToken = tokens.length === 1;

  return word.replace(/[\p{L}\p{N}]+/gu, (token, offset: number) => {
    const lower = token.toLowerCase();
    const before = word.slice(0, offset);
    const after = word.slice(offset + token.length);

    if (offset > 0 && before.endsWith('(') && after.startsWith(')') && GENDER_SUFFIX.test(`(${lower})`)) {
      return lower;
    }
    if (UPPERCASE_WORDS.has(lower)) return lower.toUpperCase();
    if (/^\p{N}/u.test(lower)) return lower; // "40h", "f4000"
    if (lower.length === 1 && after === '/' && !forceCapital) return lower; // "p/", "c/"

    const isFirstToken = !/[\p{L}\p{N}]/u.test(before);
    if (isFirstToken) {
      const isConnective = isSingleToken && !forceCapital && LOWERCASE_WORDS.has(lower);
      return isConnective ? lower : capitalize(lower);
    }
    // Depois de hífen o pedaço acompanha a palavra ("pós-vendas", "e-commerce");
    // depois de barra ou parêntese é outra palavra ("garçom/garçonete").
    return before.endsWith('-') ? lower : capitalize(lower);
  });
}

export function formatJobTitle(title: string | null | undefined): string {
  if (!title) return '';

  const text = title
    .replace(/\s+/g, ' ')
    .trim()
    // "( masculino )" → "(masculino)"
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    // "produção(homem)empresa" → "produção (homem) empresa", sem tocar em "vendedor(a)"
    .replace(/(\S)\(([^)]*)\)/g, (match, prev: string, inner: string) =>
      GENDER_SUFFIX.test(`(${inner})`) ? match : `${prev} (${inner})`,
    )
    .replace(/\)(?=[\p{L}\p{N}])/gu, ') ')
    // travessão/hífen solto sempre com um espaço de cada lado
    .replace(/\s*[-–—]\s+|\s+[-–—]\s*/g, ' - ');

  let forceNext = true; // a primeira palavra é sempre capitalizada

  return text
    .split(' ')
    .map((word) => {
      if (/^[-/|:]$/.test(word)) {
        forceNext = true;
        return word;
      }
      const formatted = capitalizeWord(word, forceNext || /^[(/|:]/.test(word));
      forceNext = /[:/|]$/.test(word);
      return formatted;
    })
    .join(' ');
}
